/**
 * Routes « publiques seulement » — redirection quand la session est ouverte
 * (LOT-RT-DA1, DA-035).
 *
 * `/login` et `/register` n'existent que dans l'arbre de routes NON authentifié
 * (`App.tsx`). Un utilisateur déjà connecté qui les visite — signet, lien
 * partagé, bouton « précédent » après connexion — tombait donc sur le `*` de
 * l'arbre authentifié, c'est-à-dire sur la **page 404**. La recette DA l'a
 * capturé (`25-login-authentifie.png`) : la seule sortie était de retaper une
 * URL.
 *
 * Ces deux chemins n'ont aucun sens une fois la session ouverte : ils renvoient
 * à l'accueil (`/` → `RoleLanding`, qui aiguille selon le rôle). `replace` :
 * la redirection ne laisse pas d'entrée dans l'historique, sinon le bouton
 * « précédent » y reviendrait en boucle.
 *
 * Le helper est exporté — et non écrit en clair dans `App.tsx` — pour que la
 * garde `publicOnlyRedirects.spec.tsx` monte les VRAIES routes de production,
 * et pas une copie qui pourrait diverger sans que rien n'échoue.
 */
import { useEffect } from 'react'
import { Navigate, Route, useLocation } from 'react-router-dom'

import { consumeLoginReturn, resolveLoginTarget } from '../utils/loginReturn'

/** Chemins réservés aux visiteurs non authentifiés. */
export const PUBLIC_ONLY_PATHS = ['/login', '/register'] as const

/** Destination des visiteurs déjà connectés : l'accueil, qui aiguille par rôle. */
export const AUTHENTICATED_HOME = '/'

/**
 * Routes à insérer dans l'arbre AUTHENTIFIÉ, avant le `*` qui rend la 404.
 * Sans elles, `/login` et `/register` y sont des chemins inconnus.
 *
 * L-NAV-8 — `home` : l'accueil du rôle connecté (`routes/roleHome.ts`,
 * `loginHomeFor`) ; défaut `/`, qui aiguille par rôle (`RoleLanding`).
 */
export function publicOnlyRedirects(userId?: string, home: string = AUTHENTICATED_HOME) {
  return PUBLIC_ONLY_PATHS.map((path) => (
    <Route
      key={path}
      path={path}
      element={
        path === '/login' ? <PostLoginRedirect userId={userId} home={home} /> : <Navigate to={home} replace />
      }
    />
  ))
}

/**
 * #262 — LA source de vérité de la cible post-connexion.
 *
 * Dès que l'état devient authentifié, `App.tsx` rend l'arbre authentifié à
 * l'adresse `/login` : c'est cette route qui choisit où aller
 * (`resolveLoginTarget` : `state.from`, ou la mémoire d'onglet du MÊME
 * utilisateur, sinon l'accueil par rôle). `LoginPage` ne navigue plus — le
 * banc a montré deux navigations concurrentes (`replace /contacts?q=dup` de la
 * page, puis `replace /` de cette route, rendue d'abord), la seconde gagnait.
 * La mémoire d'onglet est consommée une seule fois (après la redirection).
 */
function PostLoginRedirect({ userId, home }: { userId?: string; home: string }) {
  const location = useLocation()
  const target = resolveLoginTarget((location.state as { from?: unknown } | null)?.from, userId, home)
  useEffect(() => consumeLoginReturn(), [])
  return <Navigate to={target} replace />
}
